<template>
    <div class="events-carousel-wrap relative" :style="wrapStyles">
        <div class="w-full overflow-x-hidden">
            <div class="container w-full">
                <div ref="holder"
                     class="events-carousel__inner flex flex-nowrap items-stretch"
                     :style="innerStyles">
                    <a v-for="(item, index) in allItems"
                       :key="index"
                       :href="item.Link"
                       class="events-carousel__slide no-underline text-left flex flex-col shrink-0">
                        <span class="block overflow-hidden aspect-square w-full mb-[15px]"
                              :class="imageRounding">
                            <img
                                v-if="item.ImageURL"
                                :src="item.ImageURL"
                                :srcset="item.ImageURLSmall + ' 400w, ' + item.ImageURL + ' 800w'"
                                sizes="(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 85vw"
                                :alt="item.Title"
                                width="800"
                                height="800"
                                class="w-full h-full object-cover m-0"
                                loading="lazy"
                            />
                        </span>
                        <h3 class="h5 m-0 mb-[5px]">{{ item.Title }}</h3>
                        <p v-if="item.DateRange" class="mb-[10px]">{{ item.DateRange }}</p>
                    </a>
                </div>
            </div>
        </div>

        <div class="events-carousel__controls-layer absolute inset-x-0 pointer-events-none"
             :style="controlsLayerStyles">
            <div class="container">
                <div ref="sizer" class="events-carousel__controls flex justify-between items-center gap-p20">
                <button type="button"
                        :disabled="atStart"
                        @click="navigatePrev"
                        class="events-carousel__nav events-carousel__nav--prev p-0 btn-main w-[60px] md:w-[80px] h-[60px] md:h-[80px] -ml-[30px] md:-ml-[40px] flex flex-center-all"
                        :class="[imageRounding, atStart ? 'opacity-40 cursor-not-allowed' : '']">
                    <svg class="events-carousel__icon" xmlns="http://www.w3.org/2000/svg" width="80"
                         height="80" viewBox="0 0 80 80">
                        <g data-name="Group 524" transform="rotate(180 607.124 2216.624)">
                            <rect data-name="Rectangle 23" width="80" height="80" rx="40"
                                  transform="translate(1134.248 4353.248)" style="fill: none"/>
                            <g data-name="Group 47">
                                <path data-name="Line 58" transform="rotate(-90 2774.319 1619.259)"
                                      style="stroke-linejoin:round;fill:none;stroke-width:2px"
                                      d="M0 0v37.709"/>
                                <path data-name="Path 36" d="m0 0 9.252 9.252L18.5 0"
                                      transform="rotate(-90 2793.27 1609.56)"
                                      style="fill:none;stroke-width:2px"/>
                            </g>
                        </g>
                    </svg>
                    <span class="sr-only">Previous</span>
                </button>
                <button type="button"
                        :disabled="atEnd"
                        @click="navigateNext"
                        class="events-carousel__nav events-carousel__nav--next p-0 btn-main w-[60px] md:w-[80px] h-[60px] md:h-[80px] -mr-[30px] md:-mr-[40px] flex flex-center-all"
                        :class="[imageRounding, atEnd ? 'opacity-40 cursor-not-allowed' : '']">
                    <svg class="events-carousel__icon" xmlns="http://www.w3.org/2000/svg" width="80"
                         height="80" viewBox="0 0 80 80">
                        <g data-name="Group 523" transform="translate(-1134.306 -4353.248)">
                            <rect data-name="Rectangle 23" width="80" height="80" rx="40"
                                  transform="translate(1134.306 4353.248)" style="fill: none"/>
                            <g data-name="Group 47">
                                <path data-name="Line 58" transform="rotate(-90 2774.341 1619.266)"
                                      style="stroke-linejoin:round;fill:none;stroke-width:2px"
                                      d="M0 0v37.736"/>
                                <path data-name="Path 36" d="m0 0 9.258 9.258L18.517 0"
                                      transform="rotate(-90 2793.306 1609.56)"
                                      style="fill:none;stroke-width:2px"/>
                            </g>
                        </g>
                    </svg>
                    <span class="sr-only">Next</span>
                </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'EventsCarousel',
    props: {
        items: { type: String, default: '[]' },
        imageRounding: { type: String, default: 'rounded' },
        cardGap: { type: Number, default: 20 },
    },
    data() {
        return {
            allItems: [],
            widths: [],
            scrollOffset: 0,
            scrollAreaWidth: 0,
            sumWidthsValue: 0,
            imageHeight: 0,
        };
    },
    computed: {
        wrapStyles() {
            return `--events-carousel-image-center: ${Math.round(this.imageHeight / 2)}px;`;
        },
        controlsLayerStyles() {
            const center = Math.round(this.imageHeight / 2);
            return `top: ${center}px; transform: translateY(-50%);`;
        },
        scrollAllowedMax() {
            return Math.max(0, this.sumWidthsValue - this.scrollAreaWidth - this.cardGap);
        },
        offset() {
            if (this.scrollOffset > this.scrollAllowedMax) {
                return this.scrollAllowedMax;
            }
            if (this.scrollOffset < 0) {
                return 0;
            }
            return this.scrollOffset;
        },
        innerStyles() {
            return `margin-left: -${this.offset}px; gap: ${this.cardGap}px;`;
        },
        atStart() {
            return this.offset <= 0;
        },
        atEnd() {
            return this.scrollAllowedMax <= 0 || this.offset >= this.scrollAllowedMax;
        },
    },
    mounted() {
        try {
            this.allItems = JSON.parse(this.items) || [];
        } catch (e) {
            this.allItems = [];
        }
        this.$nextTick(() => {
            this.setWidths();
            window.addEventListener('resize', this.setWidths);
        });
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.setWidths);
    },
    methods: {
        setWidths() {
            if (this.$refs.sizer) {
                this.scrollAreaWidth = this.$refs.sizer.offsetWidth;
            }
            const holder = this.$refs.holder;
            if (!holder) return;
            const children = holder.children;
            this.widths = [];
            let sum = 0;
            for (let i = 0; i < children.length; i++) {
                const w = children[i].clientWidth;
                this.widths[i] = w;
                sum += w + this.cardGap;
            }
            this.sumWidthsValue = sum;
            // Image is square and equal to slide width
            this.imageHeight = this.widths[0] || 0;
            if (this.scrollOffset > this.scrollAllowedMax) {
                this.scrollOffset = this.scrollAllowedMax;
            }
        },
        navigateNext() {
            if (this.atEnd) return;
            const currentIndex = this.findCurrentIndex();
            const w = (this.widths[currentIndex] || 0) + this.cardGap;
            this.scrollOffset = Math.min(this.scrollOffset + w, this.scrollAllowedMax);
        },
        navigatePrev() {
            if (this.atStart) return;
            const currentIndex = this.findCurrentIndex();
            const prev = Math.max(0, currentIndex - 1);
            const w = (this.widths[prev] || 0) + this.cardGap;
            this.scrollOffset = Math.max(0, this.scrollOffset - w);
        },
        findCurrentIndex() {
            let acc = 0;
            for (let i = 0; i < this.widths.length; i++) {
                if (acc + (this.widths[i] || 0) / 2 >= this.offset) {
                    return i;
                }
                acc += (this.widths[i] || 0) + this.cardGap;
            }
            return this.widths.length - 1;
        },
    },
};
</script>

<style scoped>
.events-carousel__inner {
    transition: margin-left 0.3s ease-in-out;
    will-change: margin-left;
}
.events-carousel__controls > .events-carousel__nav {
    pointer-events: auto;
}
.events-carousel__slide {
    /* Mobile: ~85% of viewport */
    width: calc(85vw - 30px);
}
@media (min-width: 640px) {
    .events-carousel__slide {
        width: calc(50vw - 30px);
    }
}
@media (min-width: 1024px) {
    .events-carousel__slide {
        width: calc(33.333vw - 30px);
        max-width: 320px;
    }
}
@media (min-width: 1280px) {
    .events-carousel__slide {
        width: calc(25vw - 20px);
        max-width: 320px;
    }
}
.events-carousel__icon {
    width: 60px;
    height: 60px;
}
@media (min-width: 768px) {
    .events-carousel__icon {
        width: 80px;
        height: 80px;
    }
}
.events-carousel__icon path,
.events-carousel__icon line {
    stroke: currentColor;
}
</style>
